import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, X, AlertCircle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Notification Component
const Notification = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900';
  const icon = type === 'success' ? '✓' : '⚠';

  return (
    <div className={`rounded-lg border p-4 flex gap-3 animate-in fade-in ${bgColor}`}>
      <span className="text-xl flex-shrink-0">{icon}</span>
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

// ADD/EDIT VALET MODAL
function ValetModal({ valet, onClose, onSuccess }) {
  const [formData, setFormData] = useState(valet || {
    name: '',
    email: '',
    phone: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.name) {
      setError('Valet name is required');
      setLoading(false);
      return;
    }

    try {
      const url = valet ? `${API_URL}/api/valets/${valet.valet_id}` : `${API_URL}/api/valets`;
      const method = valet ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to save valet');
      }

      onSuccess(valet ? 'Valet updated successfully!' : 'Valet created successfully!');
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving valet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center border-b">
          <h2 className="text-xl font-bold">
            {valet ? 'Edit Valet' : 'Add New Valet'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-500 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., John Smith"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              placeholder="john@example.com"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={handleChange}
              placeholder="555-1234"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
            </select>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 justify-end pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Valet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// MAIN VALET MANAGEMENT COMPONENT
export default function ValetManagement({ onValetDeleted }) {
  const [valets, setValets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingValet, setEditingValet] = useState(null);

  useEffect(() => {
    fetchValets();
  }, []);

  const fetchValets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/valets`);
      if (response.ok) {
        const data = await response.json();
        setValets(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching valets:', error);
      setNotification({ message: 'Failed to load valets', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddValet = () => {
    setEditingValet(null);
    setShowModal(true);
  };

  const handleEditValet = (valet) => {
    setEditingValet(valet);
    setShowModal(true);
  };

  const handleDeleteValet = async (valetId) => {
    if (window.confirm('Are you sure you want to delete this valet? This will also remove their rota.')) {
      try {
        const response = await fetch(`${API_URL}/api/valets/${valetId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          setNotification({ message: 'Valet deleted successfully!', type: 'success' });
          fetchValets();
          // Trigger parent to refetch bookings
          if (onValetDeleted) {
            onValetDeleted();
          }
        } else {
          // Handle error response from backend
          const errorData = await response.json();
          setNotification({ 
            message: errorData.message || 'Failed to delete valet', 
            type: 'error' 
          });
        }
      } catch (error) {
        setNotification({ message: 'Failed to delete valet: ' + error.message, type: 'error' });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-slate-100 text-slate-800';
      case 'on_leave':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) return <div className="text-center py-8">Loading valets...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Manage your valet team. Add new valets, update their information, or remove staff members.
        </p>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Add Valet Button */}
      <button
        onClick={handleAddValet}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
      >
        <Plus className="w-4 h-4" />
        Add New Valet
      </button>

      {/* Valets Grid */}
      {valets.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg font-medium">No valets yet</p>
          <p className="text-slate-400 text-sm mt-2">Create your first valet to get started</p>
          <button
            onClick={handleAddValet}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add First Valet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {valets.map(valet => (
            <div key={valet.valet_id} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{valet.name}</h3>
                  <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(valet.status)}`}>
                    {valet.status === 'active' ? '✓ Active' : valet.status === 'on_leave' ? '⏸️ On Leave' : '✗ Inactive'}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4 text-sm text-slate-600">
                {valet.email && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">📧</span>
                    <a href={`mailto:${valet.email}`} className="text-blue-600 hover:underline">
                      {valet.email}
                    </a>
                  </div>
                )}
                {valet.phone && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">📞</span>
                    <a href={`tel:${valet.phone}`} className="text-blue-600 hover:underline">
                      {valet.phone}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => handleEditValet(valet)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition font-medium text-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteValet(valet.valet_id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition font-medium text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Valet Modal */}
      {showModal && (
        <ValetModal
          valet={editingValet}
          onClose={() => {
            setShowModal(false);
            setEditingValet(null);
          }}
          onSuccess={(message) => {
            setNotification({ message, type: 'success' });
            setShowModal(false);
            setEditingValet(null);
            fetchValets();
          }}
        />
      )}
    </div>
  );
}
